import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ExpiryWarning from '../components/ExpiryWarning';
import { formatPlanName, getPlanBadgeColor } from '../utils/planAccess';
import AccountSidebar from '../components/AccountSidebar';

// ProfileForm Component
const ProfileForm = ({ user }) => {
  const { updateUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    aboutYou: user?.about_you || '',
    mobileNo: user?.mobile_no || user?.phone || '',
    emailId: user?.email || '',
    gender: user?.gender || '',
    state: user?.state || '',
    country: user?.country || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Helper to format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Sync form data when user data changes
  React.useEffect(() => {
    setFormData({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      aboutYou: user?.about_you || '',
      mobileNo: user?.mobile_no || user?.phone || '',
      emailId: user?.email || '',
      gender: user?.gender || '',
      state: user?.state || '',
      country: user?.country || ''
    });
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Prepare the data for the API (map form fields to backend fields)
      const profileData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        about_you: formData.aboutYou,
        mobile_no: formData.mobileNo,
        gender: formData.gender,
        state: formData.state,
        country: formData.country
      };

      const result = await updateUser(profileData);

      if (result.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-r-2xl p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Profile</h1>
        <div className="flex items-center space-x-4">
          {user?.subscription_plan ? (
            <>
              <span className="text-white px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: getPlanBadgeColor(user.subscription_plan) }}>
                {formatPlanName(user.subscription_plan)}
              </span>
              <span className="text-gray-600">
                Valid till: {formatDate(user.subscription_valid_until)}
                {user.subscription_plan === 'EXPERT_TRIAL' && (
                  <span className="ml-2 text-orange-600 font-semibold">
                    (7-Day Free Trial)
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <span className="text-gray-600">No active subscription.</span>
              <Link to="/pricing" className="text-green-600 hover:underline">
                View Plans
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Message Display */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* First Name and Last Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your first name"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your last name"
              disabled={loading}
            />
          </div>
        </div>

        {/* About You */}
        <div>
          <label htmlFor="aboutYou" className="block text-sm font-medium text-gray-700 mb-2">
            About you
          </label>
          <input
            type="text"
            id="aboutYou"
            name="aboutYou"
            value={formData.aboutYou}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Tell us about yourself"
            disabled={loading}
          />
        </div>

        {/* Mobile No */}
        <div>
          <label htmlFor="mobileNo" className="block text-sm font-medium text-gray-700 mb-2">
            Mobile No
          </label>
          <input
            type="tel"
            id="mobileNo"
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your mobile number"
            disabled={loading}
          />
        </div>

        {/* Email ID */}
        <div>
          <label htmlFor="emailId" className="block text-sm font-medium text-gray-700 mb-2">
            Email ID
          </label>
          <input
            type="email"
            id="emailId"
            name="emailId"
            value={formData.emailId}
            disabled
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
            placeholder="Your email address"
          />
          <p className="text-sm text-gray-500 mt-1">Email ID cannot be edited for email login.</p>
        </div>

        {/* Gender */}
        <div>
          <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
            Gender
          </label>
          <input
            type="text"
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your gender"
            disabled={loading}
          />
        </div>

        {/* State */}
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
            State
          </label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your state"
            disabled={loading}
          />
        </div>

        {/* Country */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your country"
            disabled={loading}
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed text-white' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Updating Profile...
              </div>
            ) : (
              'Update Profile'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Main ProfilePage Component
const ProfilePage = () => {
  const { user } = useAuth();

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navbar Component */}
      <Navbar />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center py-16 lg:py-12">
        {/* Expiry Warning */}
        <div className="w-full max-w-6xl mx-auto px-4 mb-4">
          <ExpiryWarning user={user} />
        </div>
        <div className="max-w-5xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              {/* Sidebar - 1 column */}
              <AccountSidebar activePage="profile" />
              
              {/* Main Content - 3 columns */}
              <div className="md:col-span-3">
                <ProfileForm user={user} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default ProfilePage;
